import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MOCK_MODE } from '../lib/api';
import { formatPhone } from '../lib/format';
import { useInbox, useTestMode, useWaStatus } from '../lib/queries';
import type { LiveStatus } from '../lib/sse';
import { Icon, type IconName } from './icons';
import { ToneDot } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  ownerOnly?: boolean;
  badge?: number;
}

const sections: { title: string; items: NavItem[] }[] = [
  {
    title: 'Operasyon',
    items: [
      { to: '/inbox', label: 'Gelen Kutusu', icon: 'inbox' },
      { to: '/tickets', label: 'Ticket', icon: 'ticket' },
      { to: '/kisiler', label: 'Kişiler', icon: 'users' },
      { to: '/sss', label: 'SSS', icon: 'help' }
    ]
  },
  {
    title: 'Bilgi',
    items: [
      { to: '/kutuphane', label: 'Kütüphane', icon: 'book', ownerOnly: true },
      { to: '/ai-ayarlari', label: 'AI Ayarları', icon: 'sparkle', ownerOnly: true }
    ]
  },
  {
    title: 'Güvenlik',
    items: [
      { to: '/baglanti', label: 'Bağlantı', icon: 'phone', ownerOnly: true },
      { to: '/test-modu', label: 'Test Modu', icon: 'flask', ownerOnly: true },
      { to: '/audit', label: 'Audit', icon: 'list', ownerOnly: true },
      { to: '/ayarlar', label: 'Ayarlar', icon: 'gear', ownerOnly: true }
    ]
  }
];

export function Sidebar({ liveStatus = 'off' }: { liveStatus?: LiveStatus }) {
  const { user, logout } = useAuth();
  const inbox = useInbox({ filter: 'all', search: '' });
  const wa = useWaStatus();
  const testMode = useTestMode();

  const unread = inbox.data?.items.reduce((total, item) => total + item.unread, 0) ?? 0;
  const isOwner = user?.role === 'owner';
  const runtimeMode = testMode.data?.runtimeMode ?? 'TEST';

  return (
    <aside className="flex h-full flex-col border-r border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <span className="flex h-6 w-6 items-center justify-center rounded-[4px] border border-signal text-[11px] font-semibold text-signal">
          DM
        </span>
        <div className="brand-text min-w-0">
          <div className="truncate text-[13px] font-semibold leading-4">Destek Merkezi</div>
          <div className="truncate text-[11px] leading-4 text-muted">softwareeni</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2" aria-label="Ana menü">
        {sections.map((section) => (
          <div key={section.title} className="mb-3">
            <div className="nav-label px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted">
              {section.title}
            </div>
            <ul>
              {section.items
                .filter((item) => !item.ownerOnly || isOwner)
                .map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        'flex h-8 items-center gap-2 rounded-[4px] px-2 text-[13px] ' +
                        (isActive ? 'bg-signal-soft font-medium text-signal' : 'text-ink-2 hover:bg-surface-2')
                      }
                    >
                      <Icon name={item.icon} size={16} className="shrink-0" />
                      <span className="nav-label flex-1 truncate">{item.label}</span>
                      {item.to === '/inbox' && unread > 0 ? (
                        <span className="mono rounded-[4px] bg-signal px-1 text-[10px] font-medium text-white">{unread}</span>
                      ) : null}
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-line px-3 py-2">
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <ToneDot tone={wa.data?.connected ? 'ok' : wa.data?.unavailable ? 'muted' : 'danger'} />
          <span className="nav-label truncate">
            {wa.data?.connected ? 'WhatsApp bağlı' : wa.data?.unavailable ? 'WhatsApp ucu yok' : 'WhatsApp kopuk'} · {runtimeMode}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted">
          <ToneDot tone={liveStatus === 'open' ? 'ok' : liveStatus === 'reconnecting' ? 'warn' : 'muted'} />
          <span className="nav-label truncate">
            {liveStatus === 'open'
              ? 'Canlı akış bağlı'
              : liveStatus === 'reconnecting'
                ? 'Canlı akış yeniden bağlanıyor'
                : liveStatus === 'connecting'
                  ? 'Canlı akış bağlanıyor'
                  : 'Canlı akış kapalı'}
          </span>
        </div>
        {testMode.data?.allowlist[0] ? (
          <div className="nav-label mono mt-1 truncate text-[11px] text-muted">{formatPhone(testMode.data.allowlist[0].phone)}</div>
        ) : null}
      </div>

      <div className="border-t border-line px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-[10px] font-semibold text-ink-2">
            {(user?.name || '?')
              .split(' ')
              .map((part) => part[0])
              .slice(0, 2)
              .join('')}
          </span>
          <div className="nav-label min-w-0 flex-1">
            <div className="truncate text-[12px] font-medium leading-4">{user?.name}</div>
            <div className="truncate text-[11px] leading-4 text-muted">{user?.role === 'owner' ? 'Sahip' : 'Ajan'}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => void logout()} aria-label="Çıkış yap" title="Çıkış yap">
            <Icon name="logout" size={14} />
          </button>
        </div>
        {MOCK_MODE ? (
          <div className="nav-label mt-2 flex items-center gap-1 text-[10px] text-muted">
            <span className="badge badge-warn">MOCK</span>
            <span>örnek veri</span>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
